/* eslint-disable no-underscore-dangle */
import React, { useCallback, useState, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/Basics/MDBox";
import { SortableHeaderCell, TableHeader } from "./TableParts";
import { AssetRow, COLUMNS } from "./AssetRow";

const ROW_HEIGHT = 44;

/**
 * Empty state component
 */
const EmptyState = ({ t }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			height: "100%",
			minHeight: 250,
			gap: 1.5
		}}
	>
		<FolderOpenIcon sx={{ fontSize: 48, color: "text.disabled" }} />
		<Typography variant="h6" color="text.secondary" sx={{ opacity: 0.6 }}>
			{t("ASSETS.ACTIONS.emptyFolder")}
		</Typography>
		<Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
			{t("ASSETS.ACTIONS.emptyFolderDescription")}
		</Typography>
	</Box>
);

/**
 * Loading state component
 */
const LoadingState = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			height: "100%",
			minHeight: 250
		}}
	>
		<CircularProgress size={32} color="info" />
	</Box>
);

/**
 * Virtualized row renderer
 */
const Row = React.memo(({ index, style, data }) => {
	const { assets, selectedIds, onSelect, onClick, onMenuClick, onAccessClick, t, canChangeStatus } =
		data;
	const asset = assets[index];
	const isSelected = selectedIds.has(asset._id);

	return (
		<AssetRow
			asset={asset}
			selected={isSelected}
			onSelect={onSelect}
			onClick={onClick}
			onMenuClick={onMenuClick}
			onAccessClick={onAccessClick}
			t={t}
			style={style}
			canChangeStatus={canChangeStatus}
		/>
	);
});

Row.displayName = "Row";

/**
 * Modern asset table component with virtualization
 */
const AssetTable = ({
	assets = [],
	selectedRows = [],
	onSelectionChange,
	onAssetClick,
	onMenuClick,
	onAccessClick,
	loading = false,
	t,
	canChangeStatus
}) => {
	const [page, setPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(50);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: 0 }); // 0 = none, 1 = asc, -1 = desc

	const selectedIds = useMemo(() => new Set(selectedRows.map(row => row._id)), [selectedRows]);

	// Sort assets based on current sort configuration
	const sortedAssets = useMemo(() => {
		if (!sortConfig.key || sortConfig.direction === 0) {
			return assets;
		}

		const sorted = [...assets].sort((a, b) => {
			let aValue = a[sortConfig.key];
			let bValue = b[sortConfig.key];

			// Handle special cases
			if (sortConfig.key === "name") {
				aValue = (aValue || "").toLowerCase();
				bValue = (bValue || "").toLowerCase();
			} else if (sortConfig.key === "updatedAt") {
				aValue = new Date(aValue || 0).getTime();
				bValue = new Date(bValue || 0).getTime();
			} else if (sortConfig.key === "fileType") {
				aValue = (aValue || "").toLowerCase();
				bValue = (bValue || "").toLowerCase();
			} else if (sortConfig.key === "status") {
				aValue = (aValue || "").toLowerCase();
				bValue = (bValue || "").toLowerCase();
			} else if (sortConfig.key === "access") {
				aValue = (aValue || "").toLowerCase();
				bValue = (bValue || "").toLowerCase();
			}

			if (aValue < bValue) return sortConfig.direction === 1 ? -1 : 1;
			if (aValue > bValue) return sortConfig.direction === 1 ? 1 : -1;
			return 0;
		});

		return sorted;
	}, [assets, sortConfig]);

	const paginatedAssets = useMemo(() => {
		const startIndex = (page - 1) * itemsPerPage;
		return sortedAssets.slice(startIndex, startIndex + itemsPerPage);
	}, [sortedAssets, page, itemsPerPage]);

	const totalPages = Math.ceil(assets.length / itemsPerPage);

	// Handle column sorting
	const handleSort = useCallback(key => {
		setSortConfig(prev => {
			if (prev.key !== key) {
				// New column, start with ascending
				return { key, direction: 1 };
			}
			// Same column, cycle through: asc (1) -> desc (-1) -> none (0)
			let nextDirection;
			if (prev.direction === 1) {
				nextDirection = -1;
			} else if (prev.direction === -1) {
				nextDirection = 0;
			} else {
				nextDirection = 1;
			}
			return { key: nextDirection === 0 ? null : key, direction: nextDirection };
		});
		setPage(1); // Reset to first page when sorting changes
	}, []);

	const handleSelect = useCallback(
		asset => {
			const isCurrentlySelected = selectedIds.has(asset._id);
			let newSelection;

			if (isCurrentlySelected) {
				newSelection = selectedRows.filter(row => row._id !== asset._id);
			} else {
				newSelection = [...selectedRows, asset];
			}

			onSelectionChange(newSelection);
		},
		[selectedRows, selectedIds, onSelectionChange]
	);

	const handleSelectAll = useCallback(
		e => {
			if (e.target.checked) {
				onSelectionChange(paginatedAssets);
			} else {
				onSelectionChange([]);
			}
		},
		[paginatedAssets, onSelectionChange]
	);

	const handlePageChange = useCallback((event, newPage) => {
		setPage(newPage);
	}, []);

	const handleItemsPerPageChange = useCallback(event => {
		setItemsPerPage(event.target.value);
		setPage(1);
	}, []);

	const allSelected =
		paginatedAssets.length > 0 && paginatedAssets.every(asset => selectedIds.has(asset._id));
	const someSelected = paginatedAssets.some(asset => selectedIds.has(asset._id)) && !allSelected;

	// Helper to render sort indicator
	const renderSortIndicator = useCallback(
		columnKey => {
			if (sortConfig.key !== columnKey) {
				return (
					<Box
						sx={{
							position: "absolute",
							right: -2,
							top: "50%",
							transform: "translateY(-50%)",
							display: "flex",
							flexDirection: "column",
							opacity: 0.3
						}}
					>
						<ArrowDropUpIcon sx={{ fontSize: 16, mb: -1 }} />
						<ArrowDropDownIcon sx={{ fontSize: 16 }} />
					</Box>
				);
			}

			return (
				<Box
					sx={{
						position: "absolute",
						right: -2,
						top: "50%",
						transform: "translateY(-50%)"
					}}
				>
					{sortConfig.direction === 1 ? (
						<ArrowDropUpIcon sx={{ fontSize: 16 }} />
					) : (
						<ArrowDropDownIcon sx={{ fontSize: 16 }} />
					)}
				</Box>
			);
		},
		[sortConfig]
	);

	/* if (loading) {
		return <LoadingState />;
	} */

	if (assets.length === 0) {
		return <EmptyState t={t} />;
	}

	return (
		<MDBox
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				overflow: "hidden",
				borderRadius: "0 0 0.75rem 0.75rem",
				backgroundColor: "background.paper",
				boxShadow: "0 20px 27px 0 rgba(0, 0, 0, 0.05)"
			}}
		>
			{/* Table Header */}
			<TableHeader>
				<Box sx={{ width: COLUMNS.actions, flexShrink: 0, textAlign: "center" }} />

				<Box sx={{ width: COLUMNS.checkbox, flexShrink: 0 }}>
					<Checkbox
						checked={allSelected}
						indeterminate={someSelected}
						onChange={handleSelectAll}
						size="small"
						sx={{
							p: 0.5,
							color: "inherit",
							"&.Mui-checked": { color: "inherit" },
							"&.MuiCheckbox-indeterminate": { color: "inherit" }
						}}
					/>
				</Box>

				<SortableHeaderCell
					sx={{ flex: 1, pr: 2, justifyContent: "flex-start" }}
					sortable
					onClick={() => handleSort("name")}
				>
					{t("ASSETS.TABLE.name")}
					{renderSortIndicator("name")}
				</SortableHeaderCell>

				<SortableHeaderCell
					sx={{ width: COLUMNS.type, flexShrink: 0 }}
					sortable
					onClick={() => handleSort("fileType")}
				>
					{t("ASSETS.TABLE.fileType")}
					{renderSortIndicator("fileType")}
				</SortableHeaderCell>

				<SortableHeaderCell
					sx={{ width: COLUMNS.status, flexShrink: 0 }}
					sortable
					onClick={() => handleSort("status")}
				>
					{t("ASSETS.TABLE.status")}
					{renderSortIndicator("status")}
				</SortableHeaderCell>

				<Box sx={{ width: COLUMNS.FAIQ, flexShrink: 0, textAlign: "center" }}>FAIQ</Box>

				<SortableHeaderCell
					sx={{ width: COLUMNS.access, flexShrink: 0 }}
					sortable
					onClick={() => handleSort("access")}
				>
					{t("ASSETS.TABLE.access")}
					{renderSortIndicator("access")}
				</SortableHeaderCell>

				<SortableHeaderCell
					sx={{ width: COLUMNS.updatedAt, flexShrink: 0 }}
					sortable
					onClick={() => handleSort("updatedAt")}
				>
					{t("ASSETS.TABLE.updatedAt")}
					{renderSortIndicator("updatedAt")}
				</SortableHeaderCell>
			</TableHeader>

			{/* Virtualized List */}
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AutoSizer>
					{({ height, width }) => (
						<List
							height={height}
							width={width}
							itemCount={paginatedAssets.length}
							itemSize={ROW_HEIGHT}
							itemData={{
								assets: paginatedAssets,
								selectedIds,
								onSelect: handleSelect,
								onClick: onAssetClick,
								onMenuClick,
								onAccessClick,
								t,
								canChangeStatus
							}}
							overscanCount={5}
						>
							{Row}
						</List>
					)}
				</AutoSizer>
			</Box>

			{/* Pagination footer */}
			{assets.length > 0 && (
				<MDBox
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						p: 1.5,
						borderTop: "none",
						background: theme =>
							theme.palette.mode === "dark"
								? alpha(theme.palette.grey[900], 0.5)
								: alpha(theme.palette.grey[100], 0.4),
						flexShrink: 0,
						borderRadius: "0 0 0.75rem 0.75rem"
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{t("ASSETS.GRID.showingItems", {
							from: (page - 1) * itemsPerPage + 1,
							to: Math.min(page * itemsPerPage, assets.length),
							total: assets.length,
							defaultValue: `${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, assets.length)} sur ${assets.length}`
						})}
					</Typography>

					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<Typography variant="body2" color="text.secondary">
								{t("ASSETS.GRID.itemsPerPage", { defaultValue: "Par page:" })}
							</Typography>
							<FormControl size="small" sx={{ minWidth: 70 }}>
								<Select
									value={itemsPerPage}
									onChange={handleItemsPerPageChange}
									size="small"
									sx={{
										"& .MuiSelect-select": { py: 0.25, px: 1 },
										borderRadius: "0.5rem",
										backgroundColor: theme => alpha(theme.palette.grey[100], 0.3)
									}}
								>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
									<MenuItem value={100}>100</MenuItem>
									<MenuItem value={200}>200</MenuItem>
								</Select>
							</FormControl>
						</Box>

						{totalPages > 1 && (
							<Pagination
								count={totalPages}
								page={page}
								onChange={handlePageChange}
								color="info"
								size="small"
								showFirstButton
								showLastButton
								sx={{
									"& .MuiPaginationItem-root": {
										borderRadius: "0.5rem",
										fontWeight: 500
									}
								}}
							/>
						)}
					</Box>
				</MDBox>
			)}
		</MDBox>
	);
};

export default React.memo(AssetTable);
